// biome-ignore-all lint/suspicious/noImplicitAnyLet: TODO - address later
/**
 * Custom Channels Metrics Configuration
 *
 * This file extends the core metric configs with Custom Channels-specific
 * properties (like render functions for the metric cards).
 */

import type React from "react";
import type { CustomChannelMetrics } from "@/hooks/api/use-custom-channels";
import { formatDate } from "@/lib/date-formatting";
import {
  CORE_METRIC_CONFIGS,
  CORE_METRIC_DATA_TYPES,
  type CoreMetricConfig,
} from "@/lib/metrics/core-metric-configs";
import { formatCurrency } from "@/lib/metrics/format-currency";
import { formatNumber } from "@/lib/metrics/format-number";
import { formatPercentage } from "@/lib/metrics/format-percentage";

export const DRILLDOWN_LIMIT = 5000;

interface RenderMetricOptions {
  metricKey: string;
  metrics?: Record<string, unknown>;
  amountMetricKey?: string;
  secondaryMetricKey?: string;
  secondaryValueLabel?: string;
}

// Simplified renderMetric function for custom channels
function renderMetric(
  value: number | string | null | undefined,
  options: RenderMetricOptions,
): React.ReactNode {
  // Handle null/undefined values
  if (value === undefined || value === null) return "—";

  // Convert string values to numbers if needed
  const numericValue = typeof value === "string" ? parseFloat(value) : value;

  // Format the primary value
  let formattedValue;
  if (options.metricKey.includes("amount")) {
    formattedValue = formatCurrency(numericValue);
  } else if (
    options.metricKey.includes("percentage") ||
    options.metricKey.includes("rate")
  ) {
    formattedValue = formatPercentage(numericValue);
  } else if (options.metricKey.includes("date")) {
    formattedValue = numericValue ? formatDate(String(numericValue)) : "—";
  } else {
    formattedValue = formatNumber(numericValue);
  }

  // Handle secondary metric (amount or other secondary metric)
  let secondaryContentJsx: React.ReactNode = null;
  if (options && numericValue > 0) {
    // Determine which secondary metric to use (amount or other secondary metric)
    const secondaryKey = options.amountMetricKey || options.secondaryMetricKey;
    const secondaryValue =
      secondaryKey && options.metrics
        ? options.metrics[secondaryKey]
        : undefined;

    // Format the secondary value if it exists
    const formattedSecondaryValue =
      typeof secondaryValue === "number"
        ? options.amountMetricKey
          ? formatCurrency(secondaryValue)
          : formatNumber(secondaryValue)
        : null;

    if (formattedSecondaryValue) {
      secondaryContentJsx = (
        <span className="inline-flex items-center gap-1">
          <span>{formattedSecondaryValue}</span>
          {options.secondaryValueLabel && (
            <span className="font-normal whitespace-nowrap">
              {options.secondaryValueLabel}
            </span>
          )}
        </span>
      );
    }
  }

  // Always return consistent structure to avoid layout shift
  return (
    <div className="flex flex-col">
      {/* Primary Value */}
      <div className="relative flex items-start">
        <span className="text-2xl sm:text-3xl md:text-2xl lg:text-3xl font-bold leading-none">
          {formattedValue}
        </span>
      </div>

      {/* Secondary Content (if any) - always rendered for consistent height */}
      <div className="min-h-[20px] sm:min-h-[24px] md:min-h-[20px] lg:min-h-[24px] text-sm sm:text-base md:text-sm lg:text-base text-muted-foreground/50">
        {secondaryContentJsx}
      </div>
    </div>
  );
}

// Define WIP metrics
export const WIP_METRICS: Array<keyof CustomChannelMetrics> = [];

// Define hidden metrics that should be fetched but not displayed
export const HIDDEN_METRICS: Array<keyof CustomChannelMetrics> = [
  "opportunities_influenced_amount",
  "opportunities_influenced_won_amount",
  "opportunities_influenced_in_cycle_amount",
  "opportunities_created_post_engagement_amount",
  "progressed_opportunities_amount",
  "opportunities_activated_amount",
  "opportunities_activated_won_amount",
  "engagements_per_account",
  "engagements_per_deal_contact",
  "engagements_per_person",
];

// Define default metric order - core metrics for custom channels
// Note: sourced_opportunities NOT supported for custom channels (no SFDC campaign mapping)
export const DEFAULT_METRIC_ORDER: Array<keyof CustomChannelMetrics> = [
  "accounts_engaged",
  "engagements_per_account",
  "total_engagements",
  "people_engaged",
  "engagements_per_person",
  "deal_contacts_engaged",
  "engagements_per_deal_contact",
  "opportunities_influenced",
  "opportunities_influenced_amount",
  "opportunities_influenced_won",
  "opportunities_influenced_won_amount",
  "opportunities_created_post_engagement",
  "opportunities_created_post_engagement_amount",
  "opportunities_influenced_in_cycle",
  "opportunities_influenced_in_cycle_amount",
  "progressed_opportunities",
  "progressed_opportunities_amount",
  "opportunities_activated",
  "opportunities_activated_amount",
  "opportunities_activated_won",
  "opportunities_activated_won_amount",
  "accounts_activated",
  "accounts_reactivated",
  "accounts_without_opps",
];

/**
 * Metric configuration interface for Custom Channels
 * Extends CoreMetricConfig with optional render function for metric cards
 */
interface MetricConfig extends CoreMetricConfig {
  render?: (
    value: number | null,
    options: Record<string, unknown>,
  ) => React.ReactNode;
}

/**
 * Metric configuration with display properties and drilldown columns
 *
 * Custom Channels uses the core metric configs directly.
 * All drilldown columns and default sorts are now standardized in CORE_METRIC_CONFIGS.
 */
export const METRIC_CONFIG: Partial<
  Record<keyof CustomChannelMetrics, MetricConfig>
> = {
  accounts_engaged: {
    ...(CORE_METRIC_CONFIGS.accounts_engaged as MetricConfig),
    render: (value, options) => {
      const config = CORE_METRIC_CONFIGS.accounts_engaged;
      const metricsRecord = options.metrics as
        | Record<string, unknown>
        | undefined;
      const secondaryValue =
        config.secondaryMetricKey && metricsRecord
          ? (metricsRecord[config.secondaryMetricKey] as number | undefined)
          : undefined;
      return renderMetric(value, {
        metricKey: String(options.metricKey),
        metrics: metricsRecord,
        secondaryMetricKey: config.secondaryMetricKey,
        secondaryValueLabel: config.getSecondaryValueLabel?.(secondaryValue),
      });
    },
  },
  total_engagements: CORE_METRIC_CONFIGS.total_engagements as MetricConfig,
  people_engaged: {
    ...(CORE_METRIC_CONFIGS.people_engaged as MetricConfig),
    render: (value, options) => {
      const config = CORE_METRIC_CONFIGS.people_engaged;
      const metricsRecord = options.metrics as
        | Record<string, unknown>
        | undefined;
      const secondaryValue =
        config.secondaryMetricKey && metricsRecord
          ? (metricsRecord[config.secondaryMetricKey] as number | undefined)
          : undefined;
      return renderMetric(value, {
        metricKey: String(options.metricKey),
        metrics: metricsRecord,
        secondaryMetricKey: config.secondaryMetricKey,
        secondaryValueLabel: config.getSecondaryValueLabel?.(secondaryValue),
      });
    },
  },
  engagements_per_person:
    CORE_METRIC_CONFIGS.engagements_per_person as MetricConfig,
  deal_contacts_engaged: {
    ...(CORE_METRIC_CONFIGS.deal_contacts_engaged as MetricConfig),
    render: (value, options) => {
      const config = CORE_METRIC_CONFIGS.deal_contacts_engaged;
      const metricsRecord = options.metrics as
        | Record<string, unknown>
        | undefined;
      const secondaryValue =
        config.secondaryMetricKey && metricsRecord
          ? (metricsRecord[config.secondaryMetricKey] as number | undefined)
          : undefined;
      return renderMetric(value, {
        metricKey: String(options.metricKey),
        metrics: metricsRecord,
        secondaryMetricKey: config.secondaryMetricKey,
        secondaryValueLabel: config.getSecondaryValueLabel?.(secondaryValue),
      });
    },
  },
  engagements_per_deal_contact:
    CORE_METRIC_CONFIGS.engagements_per_deal_contact as MetricConfig,
  // Opportunity metrics with amount secondary metrics
  opportunities_influenced: {
    ...CORE_METRIC_CONFIGS.opportunities_influenced,
    render: (value, options) => {
      const metricsRecord = options.metrics as
        | Record<string, unknown>
        | undefined;
      return renderMetric(value, {
        metricKey: String(options.metricKey),
        metrics: metricsRecord,
        amountMetricKey: "opportunities_influenced_amount",
      });
    },
  } as MetricConfig,
  opportunities_influenced_amount:
    CORE_METRIC_CONFIGS.opportunities_influenced_amount as MetricConfig,
  opportunities_influenced_won: {
    ...CORE_METRIC_CONFIGS.opportunities_influenced_won,
    render: (value, options) => {
      const metricsRecord = options.metrics as
        | Record<string, unknown>
        | undefined;
      return renderMetric(value, {
        metricKey: String(options.metricKey),
        metrics: metricsRecord,
        amountMetricKey: "opportunities_influenced_won_amount",
      });
    },
  } as MetricConfig,
  opportunities_influenced_won_amount:
    CORE_METRIC_CONFIGS.opportunities_influenced_won_amount as MetricConfig,
  opportunities_influenced_in_cycle: {
    ...CORE_METRIC_CONFIGS.opportunities_influenced_in_cycle,
    render: (value, options) => {
      const metricsRecord = options.metrics as
        | Record<string, unknown>
        | undefined;
      return renderMetric(value, {
        metricKey: String(options.metricKey),
        metrics: metricsRecord,
        amountMetricKey: "opportunities_influenced_in_cycle_amount",
      });
    },
  } as MetricConfig,
  opportunities_influenced_in_cycle_amount:
    CORE_METRIC_CONFIGS.opportunities_influenced_in_cycle_amount as MetricConfig,
  opportunities_created_post_engagement: {
    ...CORE_METRIC_CONFIGS.opportunities_created_post_engagement,
    render: (value, options) => {
      const metricsRecord = options.metrics as
        | Record<string, unknown>
        | undefined;
      return renderMetric(value, {
        metricKey: String(options.metricKey),
        metrics: metricsRecord,
        amountMetricKey: "opportunities_created_post_engagement_amount",
      });
    },
  } as MetricConfig,
  opportunities_created_post_engagement_amount:
    CORE_METRIC_CONFIGS.opportunities_created_post_engagement_amount as MetricConfig,
  progressed_opportunities: {
    ...CORE_METRIC_CONFIGS.progressed_opportunities,
    render: (value, options) => {
      const metricsRecord = options.metrics as
        | Record<string, unknown>
        | undefined;
      return renderMetric(value, {
        metricKey: String(options.metricKey),
        metrics: metricsRecord,
        amountMetricKey: "progressed_opportunities_amount",
      });
    },
  } as MetricConfig,
  progressed_opportunities_amount:
    CORE_METRIC_CONFIGS.progressed_opportunities_amount as MetricConfig,
  opportunities_activated: {
    ...CORE_METRIC_CONFIGS.opportunities_activated,
    render: (value, options) => {
      const metricsRecord = options.metrics as
        | Record<string, unknown>
        | undefined;
      return renderMetric(value, {
        metricKey: String(options.metricKey),
        metrics: metricsRecord,
        amountMetricKey: "opportunities_activated_amount",
      });
    },
  } as MetricConfig,
  opportunities_activated_amount:
    CORE_METRIC_CONFIGS.opportunities_activated_amount as MetricConfig,
  opportunities_activated_won: {
    ...CORE_METRIC_CONFIGS.opportunities_activated_won,
    render: (value, options) => {
      const metricsRecord = options.metrics as
        | Record<string, unknown>
        | undefined;
      return renderMetric(value, {
        metricKey: String(options.metricKey),
        metrics: metricsRecord,
        amountMetricKey: "opportunities_activated_won_amount",
      });
    },
  } as MetricConfig,
  opportunities_activated_won_amount:
    CORE_METRIC_CONFIGS.opportunities_activated_won_amount as MetricConfig,
  // Account metrics without secondary values
  accounts_activated: CORE_METRIC_CONFIGS.accounts_activated as MetricConfig,
  accounts_reactivated:
    CORE_METRIC_CONFIGS.accounts_reactivated as MetricConfig,
  accounts_without_opps:
    CORE_METRIC_CONFIGS.accounts_without_opps as MetricConfig,
  engagements_per_account:
    CORE_METRIC_CONFIGS.engagements_per_account as MetricConfig,
};

/**
 * Data types for each metric (for CSV export and formatting)
 * Uses the core metric data types with any custom channel additions
 */
export const METRIC_DATA_TYPES: Partial<
  Record<keyof CustomChannelMetrics, string>
> = {
  ...CORE_METRIC_DATA_TYPES,
  // All additional custom channel metrics use the same data types defined in core
};
